import { Copy, ExternalLink } from "lucide-react";
import { Link } from "react-router";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  voiceStatus,
  voiceStatusHint,
  voiceStatusLabels,
  type VoiceStatus,
} from "./catalogState";
import type { SoundVoice, SoundVoicesPayload } from "./contracts";
import { AddVoiceDialog } from "./AddVoiceDialog";
import { DisableVoiceDialog } from "./DisableVoiceDialog";
import { EnableVoiceDialog } from "./EnableVoiceDialog";
import { VoicePreviewButton } from "./VoicePreviewButton";

const statusBadgeVariant: Record<
  VoiceStatus,
  "default" | "secondary" | "destructive" | "outline"
> = {
  live: "default",
  needs_attention: "destructive",
  pending_approval: "secondary",
  turned_off: "outline",
  not_in_product: "outline",
};

function styleLine(voice: SoundVoice) {
  const traits = voice.catalog?.normalized_profile ?? voice.labels;
  const main = [traits.gender, traits.age, traits.accent]
    .filter(Boolean)
    .map((value) => String(value).replaceAll("_", " "))
    .join(" · ");
  const secondary = [traits.locale ?? traits.language, traits.use_case]
    .filter(Boolean)
    .map((value) => String(value).replaceAll("_", " "))
    .join(" · ");
  return { main: main || "Not described", secondary };
}

export function VoiceCatalogTable({
  environment,
  payload,
  pendingVoiceDraftIds,
  voices,
  onClearFilters,
}: {
  environment: "staging" | "stg";
  payload: SoundVoicesPayload;
  pendingVoiceDraftIds: ReadonlyMap<string, string>;
  voices: SoundVoice[];
  onClearFilters?: () => void;
}) {
  return (
    <div className="relative rounded-lg border">
      <Table className="min-w-[960px] table-fixed">
        <TableHeader>
          <TableRow>
            <TableHead className="w-14">Listen</TableHead>
            <TableHead className="w-[34%]">Voice</TableHead>
            <TableHead className="w-[20%]">Style</TableHead>
            <TableHead className="w-40">Status</TableHead>
            <TableHead className="w-32">Used in Echoes</TableHead>
            <TableHead className="sticky right-0 z-20 w-40 border-l bg-background text-right shadow-sm">
              Actions
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {voices.map((voice) => {
            const displayName = voice.catalog?.display_name ?? voice.name;
            const status = voiceStatus(voice);
            const hint = voiceStatusHint(voice);
            const style = styleLine(voice);
            const selections = voice.usage?.selection_count ?? 0;
            const pendingDraftId = pendingVoiceDraftIds.get(voice.voice_id);
            return (
              <TableRow
                key={voice.voice_id}
                className={
                  status === "needs_attention" ? "bg-destructive/5" : ""
                }
              >
                <TableCell>
                  <VoicePreviewButton
                    voiceId={voice.voice_id}
                    voiceName={displayName}
                    previewUrl={voice.preview_url}
                  />
                </TableCell>
                <TableCell className="min-w-56 overflow-hidden">
                  <div className="flex items-center gap-1">
                    <span className="truncate font-medium">{displayName}</span>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="size-6 text-muted-foreground"
                      title={`Copy voice ID (${voice.voice_id})`}
                      aria-label={`Copy voice ID for ${displayName}`}
                      onClick={() =>
                        navigator.clipboard.writeText(voice.voice_id)
                      }
                    >
                      <Copy className="size-3" />
                    </Button>
                  </div>
                  {voice.usage?.retained_generated_candidate ? (
                    <div className="mt-1 flex flex-wrap items-center gap-1">
                      <Badge variant="secondary">Generated candidate</Badge>
                      {(voice.usage.generated_reuse_count ?? 0) > 0 ? (
                        <span className="text-xs text-muted-foreground">
                          Reused in {voice.usage.generated_reuse_count} Echo
                          {voice.usage.generated_reuse_count === 1 ? "" : "es"}
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                  {voice.description ? (
                    <p className="mt-1 line-clamp-2 max-w-sm text-xs text-muted-foreground">
                      {voice.description}
                    </p>
                  ) : null}
                </TableCell>
                <TableCell className="overflow-hidden text-xs">
                  <span className="capitalize">{style.main}</span>
                  {style.secondary ? (
                    <div className="text-muted-foreground">
                      {style.secondary}
                    </div>
                  ) : null}
                </TableCell>
                <TableCell className="whitespace-normal">
                  <Badge variant={statusBadgeVariant[status]}>
                    {voiceStatusLabels[status]}
                  </Badge>
                  {hint ? (
                    <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
                  ) : null}
                </TableCell>
                <TableCell className="text-xs tabular-nums">
                  {selections > 0 ? (
                    <Link
                      className="inline-flex items-center gap-1 underline-offset-4 hover:underline"
                      to={`/echoflicks_echoes?filter=${encodeURIComponent(
                        JSON.stringify({ voice_id: voice.voice_id }),
                      )}`}
                    >
                      {selections} {selections === 1 ? "Echo" : "Echoes"}
                      <ExternalLink className="size-3" />
                    </Link>
                  ) : (
                    <span className="text-muted-foreground">Not used yet</span>
                  )}
                </TableCell>
                <TableCell className="sticky right-0 z-10 w-40 border-l bg-background text-right shadow-sm">
                  {pendingDraftId ? (
                    <Button asChild size="sm" variant="outline">
                      <a href={`#sound-draft-${pendingDraftId}`}>
                        Review change
                      </a>
                    </Button>
                  ) : voice.catalog?.enabled ? (
                    <DisableVoiceDialog
                      environment={environment}
                      voice={voice}
                    />
                  ) : voice.catalog ? (
                    <EnableVoiceDialog
                      environment={environment}
                      voice={voice}
                    />
                  ) : voice.usage?.retained_generated_candidate ? (
                    <AddVoiceDialog
                      payload={payload}
                      initialVoiceId={voice.voice_id}
                      triggerLabel="Review candidate"
                      triggerVariant="outline"
                    />
                  ) : null}
                </TableCell>
              </TableRow>
            );
          })}
          {voices.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="py-12 text-center">
                <p className="text-sm font-medium">No voices match</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Try a different search or clear the filters.
                </p>
                {onClearFilters ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-3"
                    onClick={onClearFilters}
                  >
                    Clear filters
                  </Button>
                ) : null}
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </div>
  );
}
